import { AlignJustify, ArrowLeft, ArrowRight, Braces, Search, WrapText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { CodeBlock } from '@/components/codeBlock';
import { CopyButton } from '@/components/copyButton';
import { EmptyState, ErrorState, LoadingState, StateView } from '@/components/stateViews';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VirtualLogList } from '@/components/virtualLogList';
import { useLogsQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatInteger, formatTime } from '@/lib/format';
import { buildLogLookupWindow } from '@/lib/observabilityFilters';
import type { AnalyticsUrlState } from '@/lib/urlState';

type LogMode = AnalyticsUrlState['logMode'];

const lookupLabel = (mode: LogMode): string => {
  if (mode === 'failure') return 'failure correlation';
  if (mode === 'run') return 'run correlation';
  return 'known CloudWatch location';
};

const selectedLogMode = (state: AnalyticsUrlState): LogMode => {
  if (state.logGroup) return 'group';
  if (state.runId) return 'run';
  if (state.failureId) return 'failure';
  return state.logMode;
};

// The branches below intentionally keep the three bounded lookup modes and their
// data states together so each mode remains directly auditable against the URL.
// eslint-disable-next-line sonarjs/cognitive-complexity
export const LogsPage = () => {
  const { setState, state } = useRouteContext();
  const logsQuery = useLogsQuery(state);
  const [search, setSearch] = useState('');
  const [wrap, setWrap] = useState(true);
  const logWindow = buildLogLookupWindow(state);
  const mode = selectedLogMode(state);
  const hasReference = Boolean(state.failureId || state.runId || state.logGroup);
  const hasMissingLogLocation = logsQuery.isError && logsQuery.error.message.toLowerCase().includes('no cloudwatch log group is available');
  const events = (logsQuery.data?.events ?? []).filter((event) => event.message.toLowerCase().includes(search.trim().toLowerCase()));
  const source = logsQuery.data?.logStream || logsQuery.data?.logGroup || 'CloudWatch';
  const failureDetailSearch = new URLSearchParams({
    end: state.end.toISOString(),
    env: state.env,
    queueView: state.queueView,
    start: state.start.toISOString(),
  });
  if (state.failureId) failureDetailSearch.set('failureId', state.failureId);

  const selectMode = (nextMode: LogMode) => {
    setState({
      failureId: null,
      logGroup: null,
      logMode: nextMode,
      logNextToken: null,
      logStream: null,
      runId: null,
    });
  };

  return (
    <div className="grid min-w-0 gap-5">
      <StateView
        title={hasReference ? `Bounded ${lookupLabel(mode)}` : 'Choose one bounded log lookup mode'}
        tone={hasReference ? 'healthy' : 'coverage'}
      >
        Logs are read-only and limited to {logWindow.wasClamped ? 'the final 24 hours of ' : ''}the selected environment and date window.
        They do not provide broad Logs Insights search.
      </StateView>

      <Card>
        <CardHeader>
          <CardTitle>Advanced lookup</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Tabs onValueChange={(value) => selectMode(value as LogMode)} value={mode}>
            <div className="overflow-x-auto pb-1">
              <TabsList>
                <TabsTrigger value="failure">Failure</TabsTrigger>
                <TabsTrigger value="run">Run</TabsTrigger>
                <TabsTrigger value="group">Log group</TabsTrigger>
              </TabsList>
            </div>
          </Tabs>

          {mode === 'failure' ? (
            <label className="text-muted grid gap-1.5 text-xs font-semibold">
              Failure ID
              <Input
                onChange={(event) => setState({ failureId: event.target.value || null, logNextToken: null })}
                placeholder="Failure UUID"
                value={state.failureId ?? ''}
              />
            </label>
          ) : null}
          {mode === 'run' ? (
            <label className="text-muted grid gap-1.5 text-xs font-semibold">
              Run ID
              <Input
                onChange={(event) => setState({ logNextToken: null, runId: event.target.value || null })}
                placeholder="Run ID"
                value={state.runId ?? ''}
              />
            </label>
          ) : null}
          {mode === 'group' ? (
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-muted grid gap-1.5 text-xs font-semibold">
                Log group
                <Input
                  onChange={(event) => setState({ logGroup: event.target.value || null, logNextToken: null })}
                  placeholder="/aws/lambda/service"
                  value={state.logGroup ?? ''}
                />
              </label>
              <label className="text-muted grid gap-1.5 text-xs font-semibold">
                Log stream (optional)
                <Input
                  onChange={(event) => setState({ logNextToken: null, logStream: event.target.value || null })}
                  placeholder="Exact stream"
                  value={state.logStream ?? ''}
                />
              </label>
            </div>
          ) : null}

          {state.logGroup || state.logStream ? (
            <CodeBlock value={`logGroup=${state.logGroup ?? 'not set'}\nlogStream=${state.logStream ?? 'all bounded streams'}`} />
          ) : null}
        </CardContent>
      </Card>

      {!hasReference ? (
        <EmptyState title="No log reference selected" tone="coverage">
          Open logs from a failure, run, or client error when possible, or enter one known reference above.
        </EmptyState>
      ) : null}
      {logsQuery.isLoading ? <LoadingState label="Loading bounded log events" /> : null}
      {hasMissingLogLocation ? (
        <EmptyState title="No CloudWatch location was recorded" tone="coverage">
          <div className="grid justify-items-center gap-3">
            <p>
              This failure has no correlated log group or stream. For ECS failures, the event record may contain technical evidence without
              a CloudWatch location. Return to its detail or enter a known group; this page will not invent a generic destination.
            </p>
            {state.failureId ? (
              <Button asChild variant="secondary">
                <Link to={{ pathname: '/failures', search: failureDetailSearch.toString() }}>
                  <ArrowLeft aria-hidden="true" className="size-4" />
                  Return to failure detail
                </Link>
              </Button>
            ) : null}
          </div>
        </EmptyState>
      ) : null}
      {logsQuery.isError && !hasMissingLogLocation ? (
        <ErrorState message={logsQuery.error.message} onRetry={() => void logsQuery.refetch()} />
      ) : null}

      {logsQuery.data ? (
        <Card>
          <CardHeader className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-muted flex items-center gap-2 text-xs font-semibold">
                <Braces aria-hidden="true" className="size-4" />
                Contextual CloudWatch logs
              </div>
              <CardTitle className="mt-2 break-all">{logsQuery.data.logGroup}</CardTitle>
              <p className="text-muted mt-1 text-xs">
                {formatTime(logsQuery.data.startTime)} to {formatTime(logsQuery.data.endTime)} ·{' '}
                {formatInteger(logsQuery.data.events.length)}
                events
                {logsQuery.data.truncatedByBytes ? ' · truncated by byte cap' : ''}
              </p>
            </div>
            <CopyButton label="Copy visible logs" text={events.map((event) => event.message).join('\n')} />
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-muted grid min-w-64 flex-1 gap-1.5 text-xs font-semibold">
                Search loaded messages
                <span className="relative">
                  <Search aria-hidden="true" className="text-muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                  <Input
                    className="pl-9"
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search message text"
                    value={search}
                  />
                </span>
              </label>
              <Button onClick={() => setWrap((current) => !current)} variant="secondary">
                {wrap ? <AlignJustify aria-hidden="true" className="size-4" /> : <WrapText aria-hidden="true" className="size-4" />}
                {wrap ? 'Do not wrap' : 'Wrap lines'}
              </Button>
            </div>

            {events.length > 0 ? (
              <VirtualLogList events={events} source={source} wrap={wrap} />
            ) : (
              <EmptyState
                title={search ? 'No loaded log messages match this search' : 'No log events found'}
                tone={search ? 'filtered' : 'healthy'}
              >
                {search
                  ? 'Clear the local message search; it filters only this loaded bounded page.'
                  : 'CloudWatch returned no events in this window.'}
              </EmptyState>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-muted text-xs">
                {state.logNextToken ? 'A later bounded page is loaded.' : 'First bounded page.'}
                {logsQuery.data.nextToken ? ' More events are available.' : ' No later page was returned.'}
              </p>
              <div className="flex gap-2">
                {state.logNextToken ? (
                  <Button onClick={() => setState({ logNextToken: null })} variant="secondary">
                    <ArrowLeft aria-hidden="true" className="size-4" /> First page
                  </Button>
                ) : null}
                {logsQuery.data.nextToken ? (
                  <Button onClick={() => setState({ logNextToken: logsQuery.data?.nextToken ?? null })} variant="secondary">
                    Next page <ArrowRight aria-hidden="true" className="size-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
